import { ICompetitorsSnapshot } from 'legacy/lut/classes/models/partner360/ELT/IELTFinancialData'
import {
  ELT_FORM_SECTION,
  ICompetitor,
  TComplexSectionData,
} from 'legacy/lut/classes/models/partner360/ELT/IELTFormData'
import { getPreviousQuarterAndYear } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/getPreviousYearAndQuarter'
import { useCallback, useMemo } from 'react'

import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'

import { getFormedChangedComplexData } from '../../../helpers/getFormedChangedCompexData'
import { getFormedEltTranslation } from '../../../helpers/getFormedEltTranslation'
import { RoundTableLabel } from '../../roundTable/components/roundTableLabel/RoundTableLabel'
import { RoundTableSection } from '../../roundTable/components/roundTableSection/RoundTableSection'
import { ForceUpdate } from '../../styledComponents/forceUpdate/ForceUpdate'
import { StyledTable } from '../../styledComponents/styledTable/StyledTable'
import { Title } from '../../styledComponents/title/Title'

import { getCompetitorsColumns } from './getCompetitorsColumns'

import styles from './Competitors.module.scss'

interface IProps {
  fYear: number
  fQuarter: number
  data: ICompetitor[]
  updateFormData: (data: TComplexSectionData) => void
  forceUpdate: (section: ELT_FORM_SECTION) => void
  isDocVersion?: boolean
}

export const Competitors = ({
  data,
  updateFormData,
  forceUpdate,
  fYear,
  fQuarter,
  isDocVersion,
}: IProps) => {
  const onCompetitorsChange = useCallback(
    (index: number, cIndex: number, changedField: keyof ICompetitorsSnapshot) =>
      (value: string | number | null) => {
        const field = 'Competitors'
        const competitors = data[index].Competitors
        if (changedField === 'Name' && typeof value === 'string') {
          competitors[cIndex] = { ...competitors[cIndex], [changedField]: value }
        } else {
          competitors[cIndex] = { ...competitors[cIndex], [changedField]: Number(value || 0) }
        }
        const newData = getFormedChangedComplexData<ICompetitor>({
          field,
          value: competitors,
          index,
          data,
        })
        updateFormData(newData)
      },
    [data]
  )

  const { year, quarter } = useMemo(() => {
    return getPreviousQuarterAndYear(fYear, fQuarter)
  }, [fYear, fQuarter])

  const columns = useMemo(
    () =>
      getCompetitorsColumns({
        onCompetitorsChange,
        currentFQuarter: quarter,
        actualYear: year,
      }),
    [fQuarter, fYear, onCompetitorsChange]
  )

  return (
    <RoundTableSection>
      <div className={styles.root}>
        <Title
          variant="withBackground"
          text={
            isDocVersion
              ? getFormedEltTranslation('section-doc-competitors')
              : getFormedEltTranslation('section-competitors')
          }
          rightContent={
            <>
              <RoundTableLabel />
              <ForceUpdate isIcon onClick={forceUpdate} section={ELT_FORM_SECTION.COMPETITORS} />
            </>
          }
        />
        <InfoBlock
          text={getFormedEltTranslation('competitors-warning')}
          type={InfoBlockType.Warning}
        />
        <StyledTable<ICompetitor> columns={columns} items={data} classes={{ td: styles.td }} />
      </div>
    </RoundTableSection>
  )
}
